import { useCallback, Suspense, useRef, useState } from "react";
import { useAtomValue, useSetAtom } from "jotai";
import { Loadable } from "jotai/vanilla/utils/loadable";
import {
  type Key,
  Text,
  Typeahead,
  TypeaheadItem,
} from "@zapier/design-system";
import { Icon } from "@zapier/zinnia-icons";
import {
  closeSecondaryToolbarMenuSetter,
  tableAtomFamily_SUSPENDS,
  tablesSearchAtomFamily,
  takeSnapshotSetter,
  Table,
} from "@zapier/canvas-state";

import { useEmitResourceConnectEvents } from "../../../hooks/useEmitResourceConnectEvents";
import useUpdateNode from "../../../hooks/useUpdateNode";
import { CreateNewTableButton } from "../../CreateNewTableButton";
import { useFocusInput } from "../../../hooks/useFocusInput";
import { useDebouncedValue } from "../../../hooks/useDebouncedValue";
import { NewAssetButtonWrapper } from "../NewAssetButtonWrapper";
import { ErrorBoundary } from "../../ErrorBoundary";
import { NodeToolbarBottom } from "../NodeToolbarBottom";
import { MenuWrapper } from "../MenuWrapper";
import { SelectedItem } from "../SelectedItem";

import styles from "./FloatingTablesTypeahead.module.css";

type Props = {
  selectedTableId: string | undefined;
};

function TablesTypeaheadLoader() {
  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <Typeahead
        aria-label="Search tables"
        isBusy={true}
        size="medium"
        defaultSelectedKey="loading"
      >
        <TypeaheadItem key="loading" id="loading">
          Loading tables...
        </TypeaheadItem>
      </Typeahead>
    </div>
  );
}

function SelectedTableSuspended({ selectedTableId }: Props) {
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  const selectedTable = useAtomValue(tableAtomFamily_SUSPENDS(selectedTableId));

  const onRemove = useCallback(() => {
    takeSnapshot();
    updateNodeAndSave({
      data: {
        tableId: undefined,
      },
    });
  }, [takeSnapshot, updateNodeAndSave]);

  const label =
    selectedTable?.type === "success"
      ? selectedTable.data?.name
      : "Table not found";

  return (
    <SelectedItem
      label={label}
      icon={<Icon name="navTables" size={26} color="BrandOrange" />}
      clearSelection={onRemove}
    />
  );
}

function TablesTypeaheadContent() {
  const inputRef = useRef<HTMLInputElement>(null);
  const takeSnapshot = useSetAtom(takeSnapshotSetter);
  const updateNodeAndSave = useUpdateNode();
  /* ------------------------------------------------------------------
   * Search handling
   * ------------------------------------------------------------------ */
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearchTerm = useDebouncedValue(searchTerm, 300);
  const closeSecondaryMenu = useSetAtom(closeSecondaryToolbarMenuSetter);
  useFocusInput(inputRef);

  // Fetch tables (non-suspending loadable)
  // Always fetch with current search term, including empty string for initial load
  const loadableTables = useAtomValue(
    tablesSearchAtomFamily(debouncedSearchTerm),
  ) as Loadable<
    ReturnType<typeof tablesSearchAtomFamily> extends any ? any[] : never
  >;

  const isLoading = loadableTables.state === "loading";
  const hasData = loadableTables.state === "hasData";
  const tables = hasData ? loadableTables.data : [];
  const { emitEventOnTableConnect } = useEmitResourceConnectEvents();

  const handleSelectionChange = useCallback(
    (key: Key | null) => {
      takeSnapshot();
      updateNodeAndSave({
        data: {
          tableId: key,
        },
      });

      if (key) {
        emitEventOnTableConnect(String(key));
      }

      closeSecondaryMenu();
    },
    [
      takeSnapshot,
      updateNodeAndSave,
      closeSecondaryMenu,
      emitEventOnTableConnect,
    ],
  );

  return (
    <div className={styles.floatingTypeaheadWrapper}>
      <NewAssetButtonWrapper>
        <CreateNewTableButton />
      </NewAssetButtonWrapper>
      <Typeahead
        aria-label="Search tables"
        placeholder="Search tables"
        menuTrigger="focus"
        ref={inputRef}
        onInputChange={setSearchTerm}
        onChange={handleSelectionChange}
        classNamePopOver={styles.popover}
        isBusy={isLoading}
        size="medium"
      >
        {tables.map((table: Table) => {
          return (
            <TypeaheadItem
              key={table.id}
              id={table.id}
              textValue={table.name}
              className={styles.floatingTypeaheadItem}
              size="medium"
            >
              <Icon name="navTables" size={22} color="BrandOrange" />
              <div className={styles.truncateText}>
                <Text type="SmallPrint2">{table.name}</Text>
              </div>
            </TypeaheadItem>
          );
        })}
      </Typeahead>
    </div>
  );
}

export function TablesTypeahead({ selectedTableId }: Props) {
  if (selectedTableId) {
    return (
      <ErrorBoundary>
        <Suspense fallback={<TablesTypeaheadLoader />}>
          <SelectedTableSuspended selectedTableId={selectedTableId} />
        </Suspense>
      </ErrorBoundary>
    );
  }

  // No selected table – render search UI (does not suspend)
  return (
    <ErrorBoundary>
      <TablesTypeaheadContent />
    </ErrorBoundary>
  );
}

export function FloatingTablesTypeahead({ selectedTableId }: Props) {
  return (
    <NodeToolbarBottom>
      <MenuWrapper>
        <TablesTypeahead selectedTableId={selectedTableId} />
      </MenuWrapper>
    </NodeToolbarBottom>
  );
}
